import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CinematicBackground from "@/components/CinematicBackground";
import { useLang } from "@/i18n/LanguageContext";
import * as sfx from "@/i18n/sfx";

export default function Welcome() {
  const navigate = useNavigate();
  const { t, lang, chosen, muted, toggleMute } = useLang();
  const [closing, setClosing] = useState(false);
  const goneRef = useRef(false);

  const go = () => { if (goneRef.current) return; goneRef.current = true; navigate(chosen ? "/app" : "/language"); };

  const startTraining = () => {
    if (closing) return;
    sfx.playShutter();
    setClosing(true);
    setTimeout(go, 780); // بعد إغلاق فتحة العدسة
  };

  useEffect(() => {
    sfx.playOpen();
    const onInteract = () => sfx.playOpen();
    window.addEventListener("pointerdown", onInteract, { once: true });
    return () => { window.removeEventListener("pointerdown", onInteract); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rtl = lang === "ar";

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6 text-center" dir={rtl ? "rtl" : "ltr"} data-testid="welcome-page">
      <CinematicBackground scanning dense />

      {/* اجتياح ضوئي سينمائي */}
      <div className="light-sweep" />

      <FilmStrip className="top-0" />
      <FilmStrip className="bottom-0" />
      <Viewfinder />

      {/* زر كتم/تشغيل الصوت */}
      <button
        onClick={toggleMute}
        data-testid="mute-toggle"
        className="absolute top-5 right-5 z-30 w-9 h-9 rounded-full glass flex items-center justify-center text-slate-300 hover:text-gold transition-colors"
        title={muted ? t("sound_off") : t("sound_on")}
      >
        <i className={`fa-solid ${muted ? "fa-volume-xmark" : "fa-volume-high"}`} />
      </button>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="relative z-10 flex flex-col items-center w-full splash-stagger"
      >
        <div className="relative mb-7 splash-item" style={{ "--d": "0s" }}>
          <ApertureLens />
        </div>

        <div className="splash-item mb-1" style={{ "--d": "0.35s" }}>
          <LensLogo />
        </div>

        <div className="splash-item" style={{ "--d": "0.6s" }}>
          <h1 className="font-display text-3xl sm:text-4xl gold-gradient-text leading-snug mb-6">{t("appSubtitle")}</h1>
        </div>

        {/* اسم المطوّر بصورة بارزة */}
        <div className="flex flex-col items-center mb-6 splash-item" style={{ "--d": "0.9s" }} data-testid="developer-credit">
          <span className="text-slate-400 text-[11px] tracking-[0.28em] uppercase mb-1.5">{t("createdBy")}</span>
          <span className="font-display text-2xl sm:text-3xl gold-gradient-text name-glow">Mohanad Khaleel Ibrahim</span>
          <span className="text-slate-300 text-sm mt-1.5" dir="rtl" data-testid="researcher-arabic">الباحث: مهند خليل إبراهيم</span>
          <span className="mt-2.5 h-px w-36 bg-gradient-to-r from-transparent via-gold to-transparent" />
        </div>

        <div className="splash-item" style={{ "--d": "1.15s" }}>
          <p className="text-slate-300 text-sm sm:text-base max-w-md leading-relaxed mb-9 px-4">
            {t("welcome_phrase")}
          </p>
        </div>

        <div className="splash-item" style={{ "--d": "1.4s" }}>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={startTraining}
            data-testid="start-training-btn"
            className="btn-gold font-display text-lg px-12 py-4 rounded-full flex items-center gap-3"
          >
            <i className="fa-solid fa-clapperboard" />
            {t("start_btn")}
          </motion.button>
        </div>

        <div className="flex items-center gap-4 mt-8 text-slate-500 text-xs splash-item" style={{ "--d": "1.6s" }}>
          <Symbol t="ISO" /><Symbol t="f/2.8" /><Symbol t="1/50" /><Symbol t="24 FPS" /><Symbol t="5600K" />
        </div>
      </motion.div>

      <p
        className="absolute bottom-6 inset-x-0 text-center text-[11px] text-slate-500 z-10 px-4" data-testid="copyright">
        {t("rights")}
      </p>

      {/* انتقال سينمائي: إغلاق فتحة العدسة */}
      {closing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none" data-testid="iris-transition">
          <div className="iris-hole" />
          <i className="fa-solid fa-camera-retro absolute text-gold text-3xl iris-icon" />
        </div>
      )}
    </div>
  );
}

function LensLogo() {
  const blades = [0, 60, 120, 180, 240, 300];
  return (
    <div className="flex items-center gap-3" data-testid="app-logo">
      <svg viewBox="0 0 56 56" className="w-11 h-11 shrink-0">
        <g className="logo-line" fill="none" strokeLinecap="round">
          <circle cx="28" cy="28" r="24" stroke="#4ea8ff" strokeWidth="2.5" />
          <circle cx="28" cy="28" r="15" stroke="#d9b46a" strokeWidth="2" />
          {blades.map((a, i) => (
            <line key={i} x1="28" y1="28" x2="43" y2="17" stroke="#d9b46a" strokeWidth="1.5" transform={`rotate(${a} 28 28)`} />
          ))}
          <circle cx="28" cy="28" r="3.5" fill="#4ea8ff" stroke="none" />
        </g>
      </svg>
      <div className="flex flex-col items-start">
        <div className="font-display text-3xl leading-none flex items-baseline gap-1.5" dir="ltr">
          <span className="gold-gradient-text">CineARt</span>
          <span className="text-ai" style={{ textShadow: "0 0 16px rgba(78,168,255,0.6)" }}>AI</span>
        </div>
        <svg width="100%" height="8" viewBox="0 0 200 8" preserveAspectRatio="none" className="mt-1 w-full">
          <path className="logo-line" d="M2 5 Q100 9 198 4" fill="none" stroke="#d9b46a" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}

const Symbol = ({ t }) => (
  <span className="font-display text-gold/50 border border-gold/20 rounded-full px-2 py-0.5">{t}</span>
);

function ApertureLens() {
  return (
    <div className="relative w-32 h-32">
      {/* توهج أزرق خلفي */}
      <div className="absolute -inset-4 rounded-full" style={{ background: "radial-gradient(circle, rgba(78,168,255,0.30), transparent 70%)" }} />
      {/* حلقة التركيز الخارجية الدوّارة */}
      <div className="absolute inset-0 rounded-full border border-ai-blue/40 spin-slow" style={{ boxShadow: "0 0 26px rgba(78,168,255,0.4)" }}>
        <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-ai-blue" style={{ boxShadow: "0 0 8px #4ea8ff" }} />
      </div>
      {/* جسم العدسة */}
      <div className="absolute inset-3 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 35%, #12203a, #05070f 75%)", boxShadow: "inset 0 0 22px rgba(217,180,106,0.25)" }}>
        {/* شفرات فتحة العدسة (Aperture) */}
        <svg viewBox="0 0 100 100" className="w-20 h-20 aperture-spin">
          {[0, 60, 120, 180, 240, 300].map((a, i) => (
            <polygon key={i} points="50,50 80,30 84,60" fill="none" stroke="rgba(217,180,106,0.55)" strokeWidth="2" transform={`rotate(${a} 50 50)`} />
          ))}
          <circle cx="50" cy="50" r="11" fill="none" stroke="rgba(78,168,255,0.75)" strokeWidth="2" />
        </svg>
      </div>
      {/* نقطة عاكسة مركزية */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-3 h-3 rounded-full bg-white/70 focus-pulse" style={{ filter: "blur(1px)" }} />
      </div>
      {/* حلقة تركيز نابضة */}
      <div className="absolute inset-6 rounded-full border border-gold/30 focus-pulse" />
    </div>
  );
}

function FilmStrip({ className }) {
  return (
    <div className={`absolute inset-x-0 h-8 flex items-center gap-2 px-2 opacity-40 z-[2] ${className}`} style={{ background: "linear-gradient(#04060d, transparent)" }}>
      {Array.from({ length: 22 }).map((_, i) => (
        <div key={i} className="w-6 h-4 rounded-[3px] border border-gold/25 shrink-0" />
      ))}
    </div>
  );
}

function Viewfinder() {
  const corner = "absolute w-8 h-8 border-gold/40";
  return (
    <>
      <div className={`${corner} top-14 left-6 border-t-2 border-l-2 rounded-tl`} />
      <div className={`${corner} top-14 right-6 border-t-2 border-r-2 rounded-tr`} />
      <div className={`${corner} bottom-14 left-6 border-b-2 border-l-2 rounded-bl`} />
      <div className={`${corner} bottom-14 right-6 border-b-2 border-r-2 rounded-br`} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 z-[2]">
        <div className="absolute top-1/2 left-0 right-0 h-px bg-ai-blue/40" />
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-ai-blue/40" />
      </div>
    </>
  );
}
